<template>
  <div class="footer-nav-item border-b-2 pb-3">
    <div class="flex items-center justify-between gap-3">
      <Component
        :is="divOrNuxtLink(navItem.page.url)"
        v-bind="navItem.page.url ? { to: navItem.page.url, target: useIsExternalUrl(navItem.page.url) ? '_blank' : '_self' } : {}"
      >
        {{ navItem.page.title }}
      </Component>

      <button
        v-if="navItem.children?.length"
        ref="toggleButton"
        type="button"
        class="shrink-0 pl-10 typo-body-xl"
        @click="isOpen ? close() : open()"
      >
        <Transition name="fade" mode="out-in">
          <span v-if="!isOpen" class="flex items-center gap-x-1">
            <span>{{ $t('open') }} </span>
            <IconPlus class="size-4" />
          </span>
          <span v-else class="flex items-center gap-x-1">
            <span>{{ $t('close') }} </span>
            <IconMinus class="size-4" />
          </span>
        </Transition>
        <!-- <span ref="arrowDown" class="block">
          <IconArrowDown class="size-5 stroke-[6]" />
        </span> -->
      </button>
    </div>

    <!-- Collapse -->
    <div ref="collapseElement" class="h-0 overflow-hidden typo-body-xl">
      <ul class="space-y-2 pt-1">
        <li v-for="child in navItem.children" :key="child.page.id" class="slide-up first:pt-2 last:pb-2">
          <NuxtLink
            :to="child.page.url"
            :target="useIsExternalUrl(child.page.url) ? '_blank' : '_self'"
          >
            {{ child.page.title }}
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import gsap from 'gsap'

defineProps<{
  navItem: {
    page: {
      id: string
      title: string
      url: string
    }
    children: {
      page: {
        id: string
        title: string
        url: string
      }
    }[]
  }
}>()

const collapseElement = ref<HTMLElement | null>(null)
const arrowDown = ref<HTMLElement | null>(null)
const isOpen = ref(false)

let tl
function open() {
  if (tl?.isActive()) return // Skip if timeline is active

  const slideUpItems = collapseElement.value?.querySelectorAll('.slide-up') // Select all .slide-up elements

  tl = gsap.timeline({
    defaults: {
      duration: 0.3,
      ease: 'power1.inOut',
    },
  })

  tl
    .to(collapseElement.value, {
      height: 'auto',
    })
    .to(arrowDown.value, {
      rotate: 180,
    }, '<')
    .to(slideUpItems, {
      autoAlpha: 1,
      translateY: 0,
      stagger: 0.1,
    }, '-=0.2')

  isOpen.value = true
}

async function close() {
  if (!tl) {
    return
  }

  // Wait for the timeline to finish if it's active
  if (tl.isActive()) {
    await new Promise((resolve) => {
      const checkActive = () => {
        if (!tl.isActive()) {
          resolve()
        } else {
          setTimeout(checkActive, 50) // Check every 50ms
        }
      }
      checkActive()
    })
  }

  // Reverse the timeline
  tl.timeScale(1.5)
  await tl.reverse()

  isOpen.value = false
}

function divOrNuxtLink(url: string) {
  if (url) {
    return resolveComponent('NuxtLink')
  }
  return 'div'
}

onUnmounted(() => {
  if (tl) {
    tl.kill() // Kill the timeline
  }
})
</script>

<style>
.footer-nav-item .slide-up {
  visibility: hidden;
  transform: translateY(2.75rem);
}
</style>
